import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import React from "react";

const useStyle = makeStyles((theme) => ({
  root: {
    margin: "40px 0px",
  },
  title: {
    fontSize: "1.5rem !important",
    fontWeight: "500 !important",
    marginBottom: "20px !important",
    textAlign: "center",
  },

  content: {
    fontSize: "1rem",
    marginBottom: "20px !important",
  },
  subtitle: {
    fontSize: "1rem",
    marginBottom: "20px !important",
    fontWeight: "500 !important",
  },

  icon: {
    marginRight: 10,
    position: "relative",
    top: 5,
  },
  select: {
    marginBottom: 30,
  },
  footer: {
    marginTop: "-80px",
  },
  center: {
    textAlign: "center",
  },
}));

const PersonalTC = () => {
  const classes = useStyle();

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>
        Terms and Conditions (Personal Account)
      </Typography>

      <p className={classes.center}>
        These Terms and Conditions govern your use of the ST Pay personal
        account and associated services. Please read them carefully. By checking
        the "Terms and Conditions" box during registration and using ST Pay, you
        agree to be bound by these Terms. If you do not agree, you must not
        register, access or use the platform.
      </p>

      <h4>1. Definitions</h4>
      <ul>
        <li>
          "ST Pay" refers to the digital wallet and payment service platform
          operated by Green & Red Technologies Ltd, licensed as a Payment
          Service Provider (PSP) by Bangladesh Bank.
        </li>
        <li>
          "User" / "You" refers to an individual who opens and uses a personal
          ST Pay account.
        </li>
        <li>
          "Service" refers to all features, tools, and functionalities available
          through the ST Pay platform.
        </li>
        <li>
          "KYC" means Know Your Customer, a regulatory requirement for user
          identity verification.
        </li>
        <li>
          "e-Money" means electronically stored monetary value representing a
          claim on ST Pay.
        </li>
      </ul>

      <h4>2. Eligibility</h4>
      <p>You must:</p>
      <ul>
        <li>Be at least 18 years old;</li>
        <li>
          Be a resident of Bangladesh (unless otherwise approved by ST Pay);
        </li>
        <li>Submit valid identification and pass required KYC verification;</li>
        <li>Use the account only for lawful, personal purposes.</li>
      </ul>

      <h4>3. Account Registration & Security</h4>
      <ul>
        <li>
          You are responsible for safeguarding your credentials (PIN, OTP,
          passwords).
        </li>
        <li>
          ST Pay is not liable for losses caused by unauthorized access due to
          your negligence.
        </li>
        <li>
          You must notify ST Pay immediately in case of a suspected breach.
        </li>
      </ul>

      <h4>4. Nominee</h4>
      <p>A nominee is a person chosen by an ST Pay user to receive the wallet funds in case of the user’s death. Each active wallet user can add up to two nominees, and nominee details can be updated after 30 days. The same person cannot be added as a nominee more than once for the same wallet, but can be a nominee for other users’ wallets.</p>

      <h4>5. Permitted Use</h4>
      <ul>
        <li>
          ST Pay personal accounts are strictly for individual, non-commercial
          use.
        </li>
        <li>
          Use for business, high-volume transactions or merchant activity
          requires prior written approval.
        </li>
      </ul>

      <h4>6. Prohibited Activities</h4>
      <p>You must not:</p>
      <ul>
        <li>Violate laws or regulations;</li>
        <li>Engage in money laundering, fraud or terrorism financing;</li>
        <li>Use ST Pay for gambling, adult content, drugs or illegal goods;</li>
        <li>Tamper with ST Pay systems or gain unauthorized access.</li>
      </ul>

      <h4>7. Transaction Monitoring & AML Compliance</h4>
      <ul>
        <li>
          All transactions are monitored under AML/CFT guidelines of Bangladesh
          Bank and BFIU.
        </li>
        <li>
          ST Pay may report any suspicious activity to regulatory authorities
          without prior notice.
        </li>
        <li>
          Transactions may be delayed, held or cancelled for compliance
          purposes.
        </li>
      </ul>

      <h4>8. Service Limits, Holds & Suspensions</h4>
      <ul>
        <li>
          Limits may apply based on your verification level and transaction
          type.
        </li>
        <li>
          ST Pay may place holds or suspend your account:
          <ul>
            <li>During regulatory investigation;</li>
            <li>
              Upon detection of fraud, suspicious activity or security breaches;
            </li>
            <li>Upon violation of these terms.</li>
          </ul>
        </li>
      </ul>

      <h4>9. Electronic Money (E-Money)</h4>
      <ul>
        <li>
          ST Pay operates as an electronic money issuer and maintains wallet
          balances in line with the regulations of Bangladesh Bank.
        </li>
        <li>
          Wallet balances represent stored e-money, not bank deposits and are
          not interest-bearing.
        </li>
        <li>
          E-money is issued against equivalent funds received by ST Pay and
          safeguarded per regulatory standards.
        </li>
        <li>
          You acknowledge that your balance represents a stored value and can be
          used only within the scope of services ST Pay provides.
        </li>
      </ul>

      <h4>10. Adding Funds (Top-Up/Fund Load)</h4>
      <ul>
        <li>
          Funds may be added via bank accounts, debit/credit cards, MFS or
          approved channels.
        </li>
        <li>Top-up limits may apply and are subject to KYC status.</li>
        <li>
          In case of failed loading due to third-party service issues, ST Pay
          will coordinate with those providers but is not liable for delays
          beyond its control
        </li>
      </ul>

      <h4>10A. Bank Account Verification & Wallet Limit Upgrade</h4>
      <ul>
        <li>
          Users are required to link and verify a valid bank account registered
          in their name to unlock the highest transaction limits and access
          enhanced services.
        </li>
        <li>
          Until verification is successfully completed, wallet usage will remain
          limited to a cumulative transaction volume of BDT 10,000 per calendar
          month, as per ST Pay's internal policies and regulatory guidelines.
        </li>
        <li>
          ST Pay will conduct the necessary checks to validate the ownership and
          authenticity of the linked bank account. The verification process may
          take 1-3 working days from the time of submission.
        </li>
        <li>
          Upon successful verification, the user's wallet limits will be
          automatically upgraded to the maximum permissible threshold.
        </li>
        <li>
          ST Pay reserves the right to reject, delay or suspend the verification
          process in cases of suspicious activity, mismatched information or
          failure to comply with account ownership requirements.
        </li>
      </ul>

      <h4>11. Sending / Receiving Money</h4>
      <ul>
        <li>You may send and receive funds to/from other ST Pay users.</li>
        <li>Transactions are final upon confirmation.</li>
        <li>
          You are solely responsible for verifying recipient details before
          initiating a transfer.
        </li>
      </ul>

      <h4>12. Transfers to Bank / MFS / Visa Debit</h4>
      <ul>
        <li>
          ST Pay may allow transfers to linked bank accounts, Visa Debit or MFS
          providers, subject to service availability and fees.
        </li>
        <li>
          Transfers are bound by transaction cut-off times, compliance checks
          and applicable charges.
        </li>
        <li>
          ST Pay is not liable for delays caused by external providers or
          network issues.
        </li>
      </ul>

      <h4>13. Refund & Cancellation Policy</h4>
      <ul>
        <li>
          Refunds are only provided for failed or incorrect transactions
          following internal investigation.
        </li>
        <li>
          For transactions involving merchants or billers, their respective
          refund policies apply.
        </li>
        <li>
          Users may request closure of their account, subject to no pending dues
          or holds.
        </li>
      </ul>

      <h4>13A. Transaction Reversal</h4>
      <ul>
        <li>
          Once a transaction has been successfully authorized and processed by
          ST Pay, it is considered final and cannot be reversed except under
          specific conditions defined below.
        </li>
        <li>
          A transaction may be reversed by ST Pay only in the following
          scenarios:
          <ul>
            <li>
              Duplicate or accidental transaction processed due to a verified
              system error;
            </li>
            <li>
              Unauthorized or fraudulent transaction reported and verified
              through investigation;
            </li>
            <li>
              Regulatory or legal instruction from an authorized body (e.g.,
              Bangladesh Bank, BFIU);
            </li>
            <li>
              Failed delivery of services from merchants, subject to a valid
              dispute raised and resolution process.
            </li>
          </ul>
        </li>
        <li>
          Users must raise a reversal request within 24 hours of the transaction
          time, accompanied by valid details and evidence.
        </li>
        <li>
          ST Pay reserves the right to deny reversal requests that do not meet
          the above conditions or are not supported by sufficient documentation.
        </li>
        <li>
          ST Pay is not liable for reversals related to third-party service
          providers, merchants, or billers unless the issue directly involves a
          failure of the ST Pay system.
        </li>
      </ul>

      <h4>14. Fees & Charges</h4>
      <ul>
        <li>
          ST Pay may impose service charges, transaction fees, processing fees
          or any other applicable charges for the use of its services. By
          proceeding with the transaction, the user is deemed to have accepted
          the applicable charges.
        </li>
        <li>
          ST Pay reserves the right to introduce, modify, revise, or waive any
          fee or charge at its sole discretion and without prior notice.
        </li>
        <li>
          It is the user's responsibility to review the applicable charges
          before confirming any transaction. ST Pay shall not be held liable for
          any claims arising from unawareness of displayed charges.
        </li>
      </ul>

      <h4>15. Dormant & Inactive Accounts</h4>
      <ul>
        <li>Accounts inactive for 36 months may be marked as dormant.</li>
        <li>Re-activation may require re-verification.</li>
        <li>
          ST Pay reserves the right to close dormant accounts as per regulatory
          guidelines.
        </li>
      </ul>

      <h4>16. Communication & Notifications</h4>
      <ul>
        <li>
          You consent to receive communications via email, SMS, push
          notifications or in-app messages.
        </li>
        <li>
          It is your responsibility to maintain updated contact information.
        </li>
      </ul>

      <h4>17. Modification of Terms</h4>
      <ul>
        <li>
          ST Pay reserves the right to modify, amend or update these Terms and
          Conditions at any time, at its sole discretion and without prior
          notice to users.
        </li>
        <li>
          Any such modifications will become effective immediately upon being
          published on the ST Pay platform (website or mobile app).
        </li>
        <li>
          Your continued use of the ST Pay services after any such modification
          constitutes your acceptance of the revised Terms.
        </li>
        <li>
          You are responsible for reviewing the Terms periodically to stay
          informed of any changes.
        </li>
      </ul>

      <h4>18. Limitation of Liability</h4>
      <ul>
        <li>
          ST Pay shall not be liable for any indirect, incidental, special,
          punitive or consequential damages.
        </li>
        <li>
          Liability is limited to the actual amount of the disputed transaction
          under verified circumstances.
        </li>
      </ul>

      <h4>19. Force Majeure</h4>
      <p>ST Pay shall not be held liable for delays or failures caused by:</p>
      <ul>
        <li>Natural disasters, war or government actions;</li>
        <li>Regulatory changes;</li>
        <li>Cyberattacks, system failures, or network issues;</li>
        <li>Force majeure events beyond ST Pay's control.</li>
      </ul>

      <h4>20. Regulatory & ISO Compliance</h4>
      <ul>
        <li>
          ST Pay is a licensed Payment Service Provider under Bangladesh Bank.
        </li>
        <li>
          Services are operated under regulatory supervision and are aligned
          with:
          <ul>
            <li>ISO/IEC 27001:2022 (Information Security Management System)</li>
          </ul>
        </li>
        <li>
          All activities are monitored for AML, CFT and data security
          obligations.
        </li>
      </ul>

      <h4>21. Indemnity</h4>
      <p>
        In consideration of ST Pay providing services under these Terms, you
        agree to fully indemnify, defend, and hold harmless ST Pay, its
        affiliates, officers, employees and partners from and against any and
        all losses, liabilities, claims, demands, damages, costs, charges,
        penalties and expenses (including reasonable legal fees) arising from or
        in connection with:
      </p>
      <ul>
        <li>Your use or misuse of the platform or services;</li>
        <li>Any breach of these Terms and Conditions or applicable laws;</li>
        <li>
          Unauthorized access to your account or any third-party actions
          resulting from your negligence in safeguarding credentials;
        </li>
        <li>
          Violation of intellectual property, privacy, or other rights of any
          third party;
        </li>
        <li>
          Any transactions or instructions initiated using your account, whether
          authorized or not;
        </li>
        <li>
          Disclosure or leakage of confidential or sensitive information due to
          your actions or omissions.
        </li>
      </ul>

      <h4>22. Governing Law & Jurisdiction</h4>
      <p>
        These Terms are governed by the laws of the People's Republic of
        Bangladesh. Disputes shall be subject to the exclusive jurisdiction of
        courts located in Dhaka, Bangladesh.
      </p>

      <h4>23. Data Privacy & Protection</h4>
      <ul>
        <li>
          User data is collected and processed in accordance with ST Pay's
          Privacy Policy.
        </li>
        <li>
          Data may be shared with government or regulatory agencies if required
          by law.
        </li>
        <li>
          ST Pay applies industry-grade encryption and data security practices
          in line with ISO standards.
        </li>
      </ul>

      <h4>24. Customer Risks, Responsibilities and Liabilities</h4>
      <ul style={{ marginTop: "-10px" }}>
        <li>
          Customer Risks
          <ul style={{ marginBlock: "10px" }}>
            <li>
              Use of ST Pay services might inherent digital financial risks,
              including unauthorized access, technical failures or delays in
              transactions.
            </li>
            <li>
              ST Pay implements security measures, but customers should exercise
              caution and understand these risks.
            </li>
          </ul>
        </li>
        <li>
          Customer Responsibilities
          <ul style={{ marginBlock: "10px" }}>
            <li>
              Safeguard your credentials (PIN, OTP) and devices used to access
              ST Pay.
            </li>
            <li>
              Review account activity regularly and report any unauthorized
              transactions promptly.
            </li>
            <li>
              Follow ST Pay’s policies and instructions when using the wallet or
              services.
            </li>
          </ul>
        </li>
        <li>
          Customer Liabilities
          <ul style={{ marginBlock: "10px" }}>
            <li>
              Customers are liable for transactions initiated using their
              credentials unless unauthorized use is reported promptly.
            </li>
            <li>
              ST Pay’s liability is limited to system errors or negligence by ST
              Pay as described in these Terms & Conditions.
            </li>
          </ul>
        </li>
        <li>
          Dispute and Complaint Handling
          <ul style={{ marginBlock: "10px" }}>
            <li>
              Customers may report complaints via official channels such as
              email, phone or in-app support.
            </li>
            <li>
              ST Pay will investigate and resolve complaints per its grievance
              handling process.
            </li>
          </ul>
        </li>
      </ul>

      <h4>25. Intellectual Property</h4>
      <ul>
        <li>
          All logos, trademarks, content, and technology on ST Pay are the
          intellectual property of the company.
        </li>
        <li>
          You may not copy, reverse-engineer or commercially exploit any part of
          the platform.
        </li>
      </ul>

      <h4>26. Termination of Account</h4>
      <ul>
        <li>
          You may request account closure if there are no outstanding
          obligations. To close the ST Pay Account, visit the ST Pay Corporate
          Office in-person along with your NID. Upon your visit, our team will
          assist you with the account closure process. Once your account is
          successfully closed, your data will be managed in accordance with the
          guidelines of Bangladesh Bank and relevant laws of Bangladesh. This
          includes both the retention and deletion of your data, ensuring
          compliance with all regulatory requirements.
        </li>
        <li>
          ST Pay reserves the right to terminate accounts for fraud,
          non-compliance or inactivity.
        </li>
      </ul>

      {/* Limit / Annexure */}
      <h4>Annexure A</h4>
      <table className="myTable">
        <tbody>
          <tr>
            <th colSpan="3" className={classes.center}>
              Transaction Limit (Personal Wallet)
            </th>
          </tr>
          <tr>
            <td>Sl. No</td>
            <td>Description</td>
            <td>Amount (BDT)</td>
          </tr>
          <tr>
            <td>1</td>
            <td>Maximum balance at any point of time</td>
            <td>4,00,000</td>
          </tr>
          <tr>
            <td>2</td>
            <td>Add Money through any channel per transaction</td>
            <td>1,00,000</td>
          </tr>
          <tr>
            <td>3</td>
            <td>Add Money through any channel per day</td>
            <td>1,00,000</td>
          </tr>
          <tr>
            <td>4</td>
            <td>Add Money Per Month</td>
            <td>4,00,000</td>
          </tr>

          <tr>
            <th>5</th>
            <th colSpan="2">Payment (EComm / Make Payment)</th>
          </tr>
          <tr>
            <td></td>
            <td> - Per transaction limit</td>
            <td>4,00,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Daily limit</td>
            <td>4,00,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Monthly limit</td>
            <td>4,00,000</td>
          </tr>

          <tr>
            <th>6</th>
            <th colSpan="2">Send Money</th>
          </tr>
          <tr>
            <td></td>
            <td> - Per transaction limit</td>
            <td>50,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Daily limit</td>
            <td>1,00,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Monthly limit</td>
            <td>4,00,000</td>
          </tr>
          <tr>
            <th>7</th>
            <th colSpan="2">Transfer to Bank</th>
          </tr>
          <tr>
            <td></td>
            <td> - Per transaction limit</td>
            <td>50,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Daily limit</td>
            <td>50,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Monthly limit</td>
            <td>4,00,000</td>
          </tr>

          <tr>
            <th>8</th>
            <th colSpan="2">Visa Debit Card Fund Transfer (instant)</th>
          </tr>
          <tr>
            <td></td>
            <td> - Per transaction limit</td>
            <td>50,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Daily limit</td>
            <td>50,000</td>
          </tr>
          <tr>
            <td></td>
            <td> - Monthly limit</td>
            <td>4,00,000</td>
          </tr>
        </tbody>
      </table>

      <p>
        Note:
        <br />
        * ST Pay reserves the right to change the limit structure at its
        discretion.
        <br />
        * ST Pay user will receive the funds within 1 to 3
        business days through <strong>"Transfer to Bank"</strong>.
        <br />
        * Minimum and maximum add money limits per transaction may vary depending on the selected channel.

      </p>
      <h4>Annexure B</h4>
      <table className="myTable">
        <tbody>
          <tr>
            <th colSpan="3" className={classes.center}>
              Transaction Fee (Personal Wallet)
            </th>
          </tr>
          <tr>
            <td style={{ fontWeight: 600 }}>Service</td>
            <td className={classes.center}>Fee/Charge (Including VAT)</td>
          </tr>
          <tr>
            <td>Registration</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Account Maintenance</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Unlink Bank</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Utility Bill Payment</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Credit Card Bill Payment</td>
            <td>1%</td>
          </tr>
          <tr>
            <td>Add Money from Debit/Credit Card</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Add Money from Bank</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Transfer Money to Bank</td>
            <td>1%</td>
          </tr>
          <tr>
            <td>Visa Debit Card Fund Transfer (instant)</td>
            <td>1.5%</td>
          </tr>
          <tr>
            <td>Send Money (Wallet to Wallet)</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Receive Money (Wallet to Wallet)</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Mobile Recharge</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>QR Code Payment</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Transaction History</td>
            <td>Free</td>
          </tr>
          <tr>
            <td>Bank Transfer (minimum withdrawal amount BDT 100)</td>
            <td>1%</td>
          </tr>
        </tbody>
      </table>
      <p>
        * ST Pay reserves the right to change the fee structure at its
        discretion.
      </p>

      <br />

      <style>{`
        .myTable:nth-of-type(1) > tbody > tr > td:nth-of-type(1), 
        .myTable:nth-of-type(1) > tbody > tr > td:last-of-type, 
        .myTable:nth-of-type(1) > tbody > tr > th:nth-of-type(1) {
          text-align: center;
          font-weight: 600;
        }


        .myTable:nth-of-type(2) > tbody > tr > td:last-of-type {
          text-align: center;
        }

      `}</style>
    </div>
  );
};

export default PersonalTC;
